import React, { useState } from 'react';
import { Table, Button, Tag, Modal, Form, Input, Select, Switch, Popconfirm, App, Row, Col } from 'antd';
import { PlusOutlined, TeamOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import WelcomeBanner from '../../Components/common/WelcomeBanner';
import { LoadingState, ApiErrorState, EmptyState } from '../../Components/common/StateViews';
import useApiData from '../../Hooks/useApiData';
import adminUserService from '../../Services/adminUserService';
import { selectAuth } from '../../store/authSlice';
import { ROLES, ROLE_NAME_LABELS } from '../../Constants/enums';
import { formatDateTime } from '../../Utils/format';
import { toApiError } from '../../api/axiosClient';

const ROLE_OPTIONS = [ROLES.PLATFORM_ADMIN, ROLES.CIO, ROLES.CFO, ROLES.SUPPORT].map((r) => ({
  value: r,
  label: ROLE_NAME_LABELS[r] || r,
}));

// Soft, non-green chips: green is reserved for Status (active/inactive), so Role
// uses muted distinct colours instead of a solid green "button".
const ROLE_TAG_COLOR = {
  [ROLES.PLATFORM_ADMIN]: 'purple',
  [ROLES.CIO]: 'blue',
  [ROLES.CFO]: 'gold',
  [ROLES.SUPPORT]: 'cyan',
};

const AdminUsers = () => {
  const { message } = App.useApp();
  const { user } = useSelector(selectAuth);
  const { data, loading, error, reload } = useApiData(() => adminUserService.getUsers(), []);
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [statusBusyId, setStatusBusyId] = useState(null);
  const [form] = Form.useForm();

  const users = Array.isArray(data) ? data : [];

  const onCreate = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // invalid — field errors render inline
    }
    setCreating(true);
    try {
      await adminUserService.createUser(values);
      message.success('Administration user created.');
      setModalOpen(false);
      form.resetFields();
      reload();
    } catch (err) {
      const { status, message: msg } = toApiError(err);
      message.error(status === 409 ? msg || 'That email is already in use.' : msg);
    } finally {
      setCreating(false);
    }
  };

  const onToggleStatus = async (record, nextActive) => {
    setStatusBusyId(record.id);
    try {
      await adminUserService.setStatus(record.id, nextActive);
      if (nextActive) message.success('Account activated.');
      else message.warning('Account deactivated. This account can no longer sign in.');
      reload();
    } catch (err) {
      message.error(toApiError(err).message);
    } finally {
      setStatusBusyId(null);
    }
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, r) => [r.firstName, r.lastName].filter(Boolean).join(' ') || '-',
    },
    { title: 'Email', dataIndex: 'email' },
    {
      title: 'Role',
      dataIndex: 'roles',
      render: (roles) =>
        (roles || []).map((r) => (
          <Tag key={r} color={ROLE_TAG_COLOR[r] || 'default'} bordered={false} style={{ fontWeight: 500 }}>
            {ROLE_NAME_LABELS[r] || r}
          </Tag>
        )),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, r) => {
        const isSelf = user?.email && r.email && user.email.toLowerCase() === r.email.toLowerCase();
        const control = (
          <Switch
            checked={r.isActive}
            loading={statusBusyId === r.id}
            disabled={isSelf}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
        );
        if (isSelf) return control; // cannot change your own account
        return (
          <Popconfirm
            title={r.isActive ? 'Deactivate this account?' : 'Activate this account?'}
            okText="Yes"
            cancelText="No"
            onConfirm={() => onToggleStatus(r, !r.isActive)}
          >
            {control}
          </Popconfirm>
        );
      },
    },
    { title: 'Created', dataIndex: 'createdOn', render: formatDateTime },
    { title: 'Last Modified', dataIndex: 'lastModifiedOn', render: formatDateTime },
  ];

  return (
    <div>
      <WelcomeBanner
        title="Administration Users"
        subtitle="Create and manage PlatformAdmin, CIO, CFO, and Support accounts"
        icon={<TeamOutlined />}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
            Add Administration User
          </Button>
        }
      />

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ApiErrorState error={error} onRetry={reload} />
      ) : users.length === 0 ? (
        <EmptyState description="No administration users yet." />
      ) : (
        <Table rowKey="id" columns={columns} dataSource={users} pagination={{ pageSize: 10 }} scroll={{ x: 'max-content' }} />
      )}

      <Modal
        title="Add Administration User"
        open={modalOpen}
        onOk={onCreate}
        onCancel={() => setModalOpen(false)}
        confirmLoading={creating}
        okText="Create user"
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark="optional">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="firstName" label="First name" rules={[{ required: true }]}>
                <Input placeholder="First name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label="Last name" rules={[{ required: true }]}>
                <Input placeholder="Last name" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true }, { type: 'email', message: 'Enter a valid email' }]}
          >
            <Input placeholder="name@velorhr.com" autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Temporary password"
            rules={[{ required: true, message: 'Set a temporary password' }]}
          >
            <Input.Password placeholder="Set a temporary password" autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true }]}>
            <Select options={ROLE_OPTIONS} placeholder="Select a role" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminUsers;
